import { nullableArray, nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

export enum DemandPartnerStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Archived = 'ARCHIVED',
}

export enum DemandPartnerPricingType {
  RevShare = 'REVSHARE',
  ProfitShare = 'PROFIT_SHARE',
  Cpm = 'CPM',
}

export enum DemandPartnerGrossNet {
  Gross = 'GROSS',
  Net = 'NET',
}

export enum DemandPartnerCpmMetric {
  AdRequest = 'AD_REQUEST',
  FilledImpression = 'FILLED_IMPRESSION',
  AdImpression = 'AD_IMPRESSION',
}

export enum DemandPartnerSortProperty {
  DisplayName = 'displayName',
  Owner = 'owner.name',
  Status = 'status',
  BidderCode = 'bidderCode',
  CreatedOn = 'createdOn',
}

export enum DemandPartnerSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum DemandPartnerDynamicField {
  AdditionalCost = 'additionalCost',
  UseStickyPricing = 'useStickyPricing',
  UrlCleanup = 'urlCleanup',
  SkipRequestsWithoutContentUrl = 'skipRequestsWithoutContentUrl',
  NullifyContentChannelNetwork = 'nullifyContentChannelNetwork',
  CacheParams = 'cacheParams',
}

export enum DemandPartnerEditorTab {
  General = 'GENERAL',
  Pricing = 'PRICING',
  Bidding = 'BIDDING',
  DeliveryCap = 'DELIVERY_CAP',
  UserSync = 'USER_SYNC',
  Qps = 'QPS',
  Cache = 'CACHE',
  BundleDomainLists = 'BUNDLE_DOMAIN_LISTS',
}

export enum DemandPartnerTargetingList {
  Bundle = 'BUNDLE',
  Domain = 'DOMAIN',
}

export enum DemandPartnerTargetingMode {
  Include = 'INCLUDE',
  Exclude = 'EXCLUDE',
}

const statusSchema = z.preprocess(
  (value) =>
    Object.values(DemandPartnerStatus).includes(value as DemandPartnerStatus)
      ? value
      : DemandPartnerStatus.Inactive,
  z.enum(DemandPartnerStatus),
);

const pricingSchema = nullableOptional(z.enum(DemandPartnerPricingType));
const grossNetSchema = nullableOptional(z.enum(DemandPartnerGrossNet));
const cpmMetricSchema = nullableOptional(z.enum(DemandPartnerCpmMetric));

export const demandPartnerContactSchema = z.looseObject({
  id: nullableOptional(z.coerce.string()),
  name: nullableOptional(z.string()),
  role: nullableOptional(z.string()),
  email: nullableOptional(z.string()),
  phone: nullableOptional(z.string()),
});

export const demandPartnerOwnerSchema = z.looseObject({
  id: z.coerce.string(),
  name: z.string(),
  businessDomain: nullableOptional(z.string()),
  tcfVendorId: nullableOptional(z.coerce.string()),
});

export const demandPartnerAuditActorSchema = z.union([
  z.string(),
  z.looseObject({
    id: nullableOptional(z.coerce.string()),
    name: nullableOptional(z.string()),
    email: nullableOptional(z.string()),
    username: nullableOptional(z.string()),
  }),
]);

export const demandPartnerAdsTxtRecordSchema = z.looseObject({
  id: nullableOptional(z.coerce.string()),
  domain: nullableOptional(z.string()),
  publisherAccountId: nullableOptional(z.string()),
  accountType: nullableOptional(z.string()),
  certAuthority: nullableOptional(z.string()),
  certificationAuthorityId: nullableOptional(z.string()),
  comment: nullableOptional(z.string()),
  note: nullableOptional(z.string()),
  fullRecord: nullableOptional(z.string()),
});

export const demandPartnerSchema = z.looseObject({
  id: z.coerce.string(),
  displayName: nullableOptional(z.string()),
  name: nullableOptional(z.string()),
  ownerId: nullableOptional(z.coerce.string()),
  ownerName: nullableOptional(z.string()),
  owner: nullableOptional(demandPartnerOwnerSchema),
  tcfVendorId: nullableOptional(z.coerce.string()),
  bidderCode: nullableOptional(z.string()),
  address: nullableOptional(z.string()),
  status: statusSchema,
  approvalsRequired: nullableOptional(z.boolean()),
  aEnabled: nullableOptional(z.boolean()),
  avEnabled: nullableOptional(z.boolean()),
  cbEnabled: nullableOptional(z.boolean()),
  vEnabled: nullableOptional(z.boolean()),
  vavEnabled: nullableOptional(z.boolean()),
  defaultOnBundleCreation: nullableOptional(z.boolean()),
  contacts: nullableArray(demandPartnerContactSchema),
  demandPartnerParameters: nullableArray(
    z.looseObject({
      name: z.string(),
      nameCamelCase: nullableOptional(z.string()),
      uiTitle: nullableOptional(z.string()),
      defaultValue: nullableOptional(z.string()),
      required: nullableOptional(z.boolean()),
    }),
  ),
  pricingType: pricingSchema,
  revShareValue: nullableOptional(z.number()),
  revShareGrossNet: grossNetSchema,
  ioCpmCostValue: nullableOptional(z.number()),
  ioCpmCostMetric: cpmMetricSchema,
  additionalCost: nullableOptional(z.number()),
  useStickyPricing: nullableOptional(z.boolean()),
  biddingGrossNet: grossNetSchema,
  biddingTmax: nullableOptional(z.number()),
  biddingBidTTL: nullableOptional(z.number()),
  revenueCapEnabled: nullableOptional(z.boolean()),
  revenueCap: nullableOptional(z.number()),
  includeUserIdInCacheKey: nullableOptional(z.boolean()),
  userSyncRedirect: nullableOptional(z.string()),
  userSyncIframe: nullableOptional(z.string()),
  qps: nullableOptional(z.number()),
  urlCleanup: nullableOptional(z.boolean()),
  skipRequestsWithoutContentUrl: nullableOptional(z.boolean()),
  nullifyContentChannelNetwork: nullableOptional(z.boolean()),
  cacheParamsEnabled: nullableOptional(z.boolean()),
  cacheUa: nullableOptional(z.boolean()),
  cacheIp: nullableOptional(z.boolean()),
  cacheBundle: nullableOptional(z.boolean()),
  cacheCountry: nullableOptional(z.boolean()),
  cacheCity: nullableOptional(z.boolean()),
  includeBundles: nullableOptional(z.boolean()),
  includeDomains: nullableOptional(z.boolean()),
  bundleListCount: nullableOptional(z.number().int().nonnegative()),
  domainListCount: nullableOptional(z.number().int().nonnegative()),
  demandIntegrationCnt: nullableOptional(z.number().int().nonnegative()),
  demandIntegrationsCnt: nullableOptional(z.number().int().nonnegative()),
  approvedCnt: nullableOptional(z.number().int().nonnegative()),
  rejectedCnt: nullableOptional(z.number().int().nonnegative()),
  pendingCnt: nullableOptional(z.number().int().nonnegative()),
  submittedCnt: nullableOptional(z.number().int().nonnegative()),
  notAvailableCnt: nullableOptional(z.number().int().nonnegative()),
  notSubmittedCnt: nullableOptional(z.number().int().nonnegative()),
  totalSites: nullableOptional(z.number().int().nonnegative()),
  secondaryId: nullableOptional(z.coerce.string()),
  adsTxtRecords: nullableArray(demandPartnerAdsTxtRecordSchema),
  network: nullableOptional(
    z.looseObject({
      id: z.coerce.string(),
      name: nullableOptional(z.string()),
    }),
  ),
  createdOn: nullableOptional(z.string()),
  updatedOn: nullableOptional(z.string()),
  createdBy: nullableOptional(demandPartnerAuditActorSchema),
  updatedBy: nullableOptional(demandPartnerAuditActorSchema),
});

export const demandPartnersPageSchema = z.looseObject({
  content: z.array(demandPartnerSchema),
  totalElements: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative().optional(),
  number: z.number().int().nonnegative().optional(),
  size: z.number().int().positive().optional(),
});

export const demandPartnerOwnersPageSchema = z.union([
  z.array(demandPartnerOwnerSchema),
  z.looseObject({
    content: z.array(demandPartnerOwnerSchema),
    totalElements: z.number().int().nonnegative().optional(),
  }),
]);

const bidderConfigEntrySchema = z.looseObject({
  visible: z.array(z.string()).optional().default([]),
  required: z.array(z.string()).optional().default([]),
  defaults: z.record(z.string(), z.unknown()).optional().default({}),
  cacheParamsPreset: z.array(z.string()).optional(),
});

export const demandPartnerBidderConfigSchema = z.looseObject({
  default: bidderConfigEntrySchema,
  bidders: z.record(z.string(), bidderConfigEntrySchema).default({}),
});

export const demandPartnerStatusCountsSchema = z.preprocess(
  (value) => {
    if (!Array.isArray(value)) return value;
    return Object.fromEntries(
      value
        .filter((item): item is Record<string, unknown> =>
          Boolean(item && typeof item === 'object'),
        )
        .map((item) => [String(item['status']), Number(item['count'] ?? 0)]),
    );
  },
  z
    .object({
      [DemandPartnerStatus.Active]: z.number().int().nonnegative().default(0),
      [DemandPartnerStatus.Inactive]: z.number().int().nonnegative().default(0),
      [DemandPartnerStatus.Archived]: z.number().int().nonnegative().default(0),
    })
    .partial(),
);

export interface DemandPartnersFilter {
  searchTerm: string;
  statuses: DemandPartnerStatus[];
  page: number;
  size: number;
  sortProperty: DemandPartnerSortProperty | null;
  sortDirection: DemandPartnerSortDirection;
}

export interface DemandPartnerMutationPayload {
  displayName: string;
  ownerId?: string;
  clearOwner?: true;
  tcfVendorId: string;
  bidderCode: string | null;
  address: string | null;
  status: DemandPartnerStatus;
  approvalsRequired: boolean;
  contacts: Array<{
    id?: string;
    name: string;
    role: string | null;
    email: string | null;
    phone: string | null;
  }>;
  pricingType: DemandPartnerPricingType | null;
  revShareValue: number | null;
  revShareGrossNet: DemandPartnerGrossNet;
  ioCpmCostValue: number | null;
  ioCpmCostMetric: DemandPartnerCpmMetric | null;
  additionalCost: number | null;
  useStickyPricing: boolean;
  biddingGrossNet: DemandPartnerGrossNet;
  biddingTmax: number | null;
  biddingBidTTL: number | null;
  revenueCapEnabled: boolean;
  revenueCap: number | null;
  includeUserIdInCacheKey: boolean;
  userSyncRedirect: string | null;
  userSyncIframe: string | null;
  qps: number | null;
  urlCleanup: boolean;
  skipRequestsWithoutContentUrl: boolean;
  nullifyContentChannelNetwork: boolean;
  cacheParamsEnabled: boolean;
  cacheUa: boolean;
  cacheIp: boolean;
  cacheBundle: boolean;
  cacheCountry: boolean;
  cacheCity: boolean;
  includeBundles: boolean;
  includeDomains: boolean;
}

export type DemandPartner = z.infer<typeof demandPartnerSchema>;
export type DemandPartnerContact = z.infer<typeof demandPartnerContactSchema>;
export type DemandPartnerOwner = z.infer<typeof demandPartnerOwnerSchema>;
export type DemandPartnerAdsTxtRecord = z.infer<typeof demandPartnerAdsTxtRecordSchema>;
export type DemandPartnerBidderConfigEntry = z.infer<typeof bidderConfigEntrySchema>;
export type DemandPartnerBidderConfig = z.infer<typeof demandPartnerBidderConfigSchema>;
export type DemandPartnersPage = z.infer<typeof demandPartnersPageSchema>;

export const demandPartnerPageSizes = [5, 10, 25, 50, 100] as const;

export const defaultDemandPartnersFilter: DemandPartnersFilter = {
  searchTerm: '',
  statuses: [DemandPartnerStatus.Active],
  page: 0,
  size: 10,
  sortProperty: DemandPartnerSortProperty.DisplayName,
  sortDirection: DemandPartnerSortDirection.Asc,
};

export function isDemandPartnerSortProperty(value: string): value is DemandPartnerSortProperty {
  return Object.values(DemandPartnerSortProperty).includes(value as DemandPartnerSortProperty);
}
